import Tesseract from 'tesseract.js';

const IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/bmp',
  'image/gif',
]);

const DOC_FIELD_MAP = {
  tinFiles: { docType: 'tin', formKey: 'tinNumber', nameFormKey: 'tinNameOnId' },
  sssFiles: { docType: 'sss', formKey: 'sssNumber', nameFormKey: 'sssNameOnId' },
  philHealthFiles: { docType: 'philhealth', formKey: 'philHealthNumber', nameFormKey: 'philHealthNameOnId' },
  pagIbigFiles: { docType: 'pagibig', formKey: 'pagIbigNumber', nameFormKey: 'pagIbigNameOnId' },
};

const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

const formatTinNumber = (value) => {
  const digits = digitsOnly(value);
  if (digits.length === 12) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}-${digits.slice(9, 12)}`;
  }
  if (digits.length === 9) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}`;
  }
  return digits;
};

const formatSssNumber = (value) => {
  const digits = digitsOnly(value);
  if (digits.length === 10) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 9)}-${digits.slice(9)}`;
  }
  return digits;
};

const formatPhilHealthNumber = (value) => {
  const digits = digitsOnly(value);
  if (digits.length === 12) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 11)}-${digits.slice(11)}`;
  }
  return digits;
};

const formatPagIbigNumber = (value) => {
  const digits = digitsOnly(value);
  if (digits.length === 12) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8, 12)}`;
  }
  return digits;
};

export const formatGovernmentIdNumber = (docType, value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';

  switch (docType) {
    case 'tin':
      return formatTinNumber(normalized);
    case 'sss':
      return formatSssNumber(normalized);
    case 'philhealth':
      return formatPhilHealthNumber(normalized);
    case 'pagibig':
      return formatPagIbigNumber(normalized);
    default:
      return normalized;
  }
};

const pickBestMatch = (matches, preferredLengths = []) => {
  if (!matches.length) return '';

  const ranked = [...matches].sort((a, b) => {
    const aDigits = digitsOnly(a);
    const bDigits = digitsOnly(b);
    const aPreferred = preferredLengths.includes(aDigits.length) ? 1 : 0;
    const bPreferred = preferredLengths.includes(bDigits.length) ? 1 : 0;
    if (aPreferred !== bPreferred) return bPreferred - aPreferred;
    return bDigits.length - aDigits.length;
  });

  return ranked[0];
};

const extractFromText = (docType, rawText) => {
  const text = String(rawText || '').toUpperCase();
  const compact = text.replace(/\s+/g, ' ');

  if (docType === 'tin') {
    const labeled = compact.match(/TIN[\s:.-]*([0-9]{3}[-\s]?[0-9]{3}[-\s]?[0-9]{3}(?:[-\s]?[0-9]{3})?)/i);
    if (labeled?.[1]) return formatTinNumber(labeled[1]);

    const matches = [...compact.matchAll(/\b(\d{3}[-\s]?\d{3}[-\s]?\d{3}(?:[-\s]?\d{3})?)\b/g)].map((m) => m[1]);
    return formatTinNumber(pickBestMatch(matches, [9, 12]));
  }

  if (docType === 'sss') {
    const labeled = compact.match(/SSS[\s:.-]*([0-9]{2}[-\s]?[0-9]{7}[-\s]?[0-9])/i);
    if (labeled?.[1]) return formatSssNumber(labeled[1]);

    const matches = [...compact.matchAll(/\b(\d{2}[-\s]?\d{7}[-\s]?\d)\b/g)].map((m) => m[1]);
    return formatSssNumber(pickBestMatch(matches, [10]));
  }

  if (docType === 'philhealth') {
    const labeled = compact.match(/(?:PHIL[\s-]?HEALTH|PHIC|PIN)[\s:.-]*([0-9]{2}[-\s]?[0-9]{9}[-\s]?[0-9])/i);
    if (labeled?.[1]) return formatPhilHealthNumber(labeled[1]);

    const matches = [...compact.matchAll(/\b(\d{2}[-\s]?\d{9}[-\s]?\d)\b/g)].map((m) => m[1]);
    return formatPhilHealthNumber(pickBestMatch(matches, [12]));
  }

  if (docType === 'pagibig') {
    const labeled = compact.match(/(?:PAG[\s-]?IBIG|HDMF|MID)[\s:.-]*([0-9]{4}[-\s]?[0-9]{4}[-\s]?[0-9]{4})/i);
    if (labeled?.[1]) return formatPagIbigNumber(labeled[1]);

    const matches = [...compact.matchAll(/\b(\d{4}[-\s]?\d{4}[-\s]?\d{4})\b/g)].map((m) => m[1]);
    return formatPagIbigNumber(pickBestMatch(matches, [12]));
  }

  return '';
};

const cleanExtractedName = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/[|]/g, ' ')
    .trim();

export const extractNameFromText = (rawText) => {
  const lines = String(rawText || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  for (const line of lines) {
    const labeled = line.match(
      /(?:NAME|MEMBER\s*NAME|REGISTERED\s*NAME|NOMBRE|APELYIDO|GIVEN\s*NAME)[:\s.-]+(.+)/i
    );
    if (labeled?.[1]) {
      const cleaned = cleanExtractedName(labeled[1]);
      if (cleaned.split(' ').length >= 2) return cleaned;
    }
  }

  const candidates = lines
    .map((line) => cleanExtractedName(line))
    .filter((line) => /^[A-Za-z][A-Za-z\s.'-]{4,}$/.test(line))
    .filter((line) => line.split(' ').length >= 2)
    .sort((a, b) => b.length - a.length);

  return candidates[0] || '';
};

export const getUploadOcrMapping = (field) => DOC_FIELD_MAP[field] || null;

export const canReadIdDocument = (file) => {
  if (!file) return false;
  if (IMAGE_TYPES.has(file.type)) return true;
  return /\.(jpe?g|png|webp|bmp|gif)$/i.test(file.name || '');
};

export const extractIdNumberFromFile = async (file, docType, onProgress) => {
  if (!file || !docType) {
    return { number: '', rawText: '', supported: false };
  }

  if (!canReadIdDocument(file)) {
    return { number: '', rawText: '', supported: false };
  }

  const result = await Tesseract.recognize(file, 'eng', {
    logger: (message) => {
      if (typeof onProgress === 'function' && message?.status === 'recognizing text') {
        onProgress(Math.round((message.progress || 0) * 100));
      }
    },
  });

  const rawText = result?.data?.text || '';
  const number = extractFromText(docType, rawText);
  const name = extractNameFromText(rawText);

  return {
    number,
    name,
    rawText,
    supported: true,
  };
};

export const getMiddleInitial = (middleName) => {
  const trimmed = String(middleName || '').trim();
  if (!trimmed) return '';
  return trimmed.charAt(0).toUpperCase();
};
